import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppStore } from '../../store/app'
import type { ChatMessage, ChatAttachment } from '../../store/types'
import { sendChatMessage, sendListChatMessages, sendListChatThread, sendMarkChatThreadRead, sendSetChatThreadFollow, sendChatReplyToChannel, wsRequest, addChatReaction, removeChatReaction, uploadChatAttachment, editChatMessage, sendMarkChatRoomRead } from '../../api/ws'
import { bridge } from '../../bridge'
import { NavBar, NavBarButton } from '../../components/NavBar'
import { StarIcon } from '../../components/icons'
import { MessageBubble, type BubbleVariant } from '../../components/MessageBubble'
import { SwipeToReply } from '../../components/SwipeToReply'
import { useStickToBottom } from '../../components/useStickToBottom'
import { parseServerTime } from '../../util/serverTime'
import { MessageActionMenu } from './MessageActionMenu'
import { MessageAttachments, PendingImagePreview } from './MessageAttachments'
import { normalizeUpload } from './imagePrep'
import { EmojiPicker, FormatBar, formatSelection, insertAtSelection, type FormatKind } from './ComposerTools'
import {
  displayTokensToRefs,
  refsToDisplay,
  mentionsSelf,
  HELPER_MEMBER,
  type MentionResolver,
  type MentionMember,
} from './mentions'
import { tempAgentAvailability } from '../../store/tempAgent'
import { primeKeyboard, isKeyboardPrimer } from '../../components/keyboardPrimer'
import { setMyReaction, myReactionEmoji } from './reactions'
import { getChatDraft, setChatDraft } from './drafts'
import { ThreadSummaryRow } from './ThreadSummaryRow'
import { channelMessages, threadMessages, replyCountLabel, draftKey } from './threads'
import { Markdown } from '../permission/Markdown'
import './ChatRoomScreen.css'

// 8 MB of base64 ≈ 6 MB of file — matches the relay's per-attachment ceiling.
const MAX_ATTACHMENT_B64 = 8 * 1024 * 1024

// Unique suffix for an optimistic message's local id. Not cryptographic — just
// needs to be collision-free within a session so the bubble is addressable until
// the server row replaces it.
function localID(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2)
}

// Wire shape from the server — created_at is a string; the store keeps
// timestamp (ms) instead. We convert on ingest below.
type WireChatMessage = Omit<ChatMessage, 'timestamp'> & { created_at: string }

type MemberEntry = MentionMember

export function ChatRoomScreen({
  roomID,
  roomName,
  threadRootID,
  focusOnOpen,
  asPane,
}: {
  roomID: string
  roomName: string
  // Set → this screen is a THREAD (Slack-style, full screen over its room):
  // the thread's first message, then its replies, and a composer that replies
  // in the thread. Unset → the room's channel. See threads.ts.
  threadRootID?: string
  // Open with the composer focused (swipe-right / "Reply in thread").
  focusOnOpen?: boolean
  // Shown as a side panel beside its room on a wide window (NavStack), rather
  // than full screen: the leading button closes the panel instead of "Back".
  asPane?: boolean
}) {
  const inThread = !!threadRootID
  const popNav = useAppStore((s) => s.popNav)
  const pushNav = useAppStore((s) => s.pushNav)
  const pushSheet = useAppStore((s) => s.pushSheet)
  const humanUserID = useAppStore((s) => s.humanUserID)
  const organizationID = useAppStore((s) => s.organizationID)
  const agentInstances = useAppStore((s) => s.agentInstances)
  // @helper is offered exactly when the "Temp Agent" button would be enabled.
  const hosts = useAppStore((s) => s.hosts)
  const tempAgentDefaults = useAppStore((s) => s.tempAgentDefaults)
  const canSummonHelper = tempAgentAvailability(hosts, tempAgentDefaults).canSpawnTemp
  // Every message we hold for this room: channel messages AND thread replies.
  const allMessages = useAppStore((s) => s.chatMessages[roomID])
  // The list this screen scrolls: the channel view, or this thread's replies.
  // (The thread's first message renders above them; see threadRoot.)
  // Threads in this room you follow that have unread replies — their summary
  // rows show as unread. From the same list the inbox and room rows use.
  const attentionThreads = useAppStore((s) => s.attentionThreads)
  const unreadThreadRoots = useMemo(
    () => new Set(attentionThreads.filter((t) => t.room_id === roomID).map((t) => t.root_id)),
    [attentionThreads, roomID],
  )
  const { messages, threadRoot } = useMemo(() => {
    const all = allMessages ?? []
    if (!threadRootID) return { messages: channelMessages(all), threadRoot: undefined }
    const t = threadMessages(all, threadRootID)
    return { messages: t.replies, threadRoot: t.root }
  }, [allMessages, threadRootID])
  const appendChatMessages = useAppStore((s) => s.appendChatMessages)
  const prependChatMessages = useAppStore((s) => s.prependChatMessages)
  const upsertChatMessages = useAppStore((s) => s.upsertChatMessages)
  const updateChatMessage = useAppStore((s) => s.updateChatMessage)
  const confirmOptimisticMessage = useAppStore((s) => s.confirmOptimisticMessage)
  const removeChatMessage = useAppStore((s) => s.removeChatMessage)
  const clearChatUnread = useAppStore((s) => s.clearChatUnread)

  // Seeded from this room's (or thread's) unsent draft, so leaving and coming
  // back (Back button or edge swipe) keeps what was typed. See drafts.ts.
  const dKey = draftKey(roomID, threadRootID)
  const [text, setText] = useState(() => getChatDraft(dKey))
  // Thread composer: "Also send to #room". Resets after each send, as in Slack.
  const [alsoInChannel, setAlsoInChannel] = useState(false)
  // Thread screen: whether you're notified about this thread's replies. Unknown
  // (null) until the thread loads; the toggle hides until then.
  const [threadFollowing, setThreadFollowing] = useState<boolean | null>(null)
  const pushToast = useAppStore((s) => s.pushToast)
  const [sending, setSending] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  // First-page load: 'loading' until it lands, 'failed' if it didn't (so an
  // empty screen never claims "No messages yet" when the load just failed).
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'failed'>('loading')
  const [loadingMore, setLoadingMore] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [attachError, setAttachError] = useState<string | null>(null)
  // Attachments uploaded but not yet posted; the send() call links them by id.
  const [pendingAttachments, setPendingAttachments] = useState<ChatAttachment[]>([])
  // Composer tools: emoji picker + markdown formatting bar visibility.
  const [showEmoji, setShowEmoji] = useState(false)
  const [showFormatBar, setShowFormatBar] = useState(false)
  // Live markdown preview of the current draft (toggled from the format bar).
  const [showPreview, setShowPreview] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [activeMsg, setActiveMsg] = useState<ChatMessage | null>(null)

  // Pin to the bottom only while the reader is already there; once they scroll
  // up to read, new messages stop yanking their position and raise a "New
  // messages" pill instead. Keyed on the LAST message's id (not length) so that
  // prepending older history via loadMore never trips the badge. Room switch
  // keyed on roomID re-pins.
  const lastMessageID = messages.length ? messages[messages.length - 1].id : ''
  const { scrollRef, onScroll, hasNew, scrollToBottom } = useStickToBottom(
    lastMessageID,
    dKey,
  )
  // When set, the composer is editing this existing message instead of sending a
  // new one; send() branches on it.
  const [editingMsg, setEditingMsg] = useState<ChatMessage | null>(null)

  // @mention state machine
  const [mentionState, setMentionState] = useState<null | { anchor: number; query: string }>(null)
  const [mentionHighlight, setMentionHighlight] = useState(0)
  const [members, setMembers] = useState<MemberEntry[] | null>(null)
  const [memberLoading, setMemberLoading] = useState(false)
  // The viewer's own display name (filtered out of the picker roster, so kept
  // separately) — used to render your own @mention as your name, not "you".
  const [selfName, setSelfName] = useState<string | null>(null)

  // Drop the cached @mention roster whenever the selected household
  // changes underneath us. ensureMembers() resolves names against the
  // *current* household and then caches for the life of the screen, so
  // without this a room opened from a cross-household push notification
  // (which navigates immediately and switches household a round-trip
  // later) could pin the previous household's people and agents into the
  // autocomplete — offering names that don't exist in the room you're
  // typing into.
  useEffect(() => {
    setMembers(null)
    setSelfName(null)
  }, [organizationID])

  // Which room/thread is open (for push suppression) is reported by App from
  // the nav stack — see nav/activeChat.ts — not per screen.
  const connectionStatus = useAppStore((s) => s.connectionStatus)

  // Tell the server how far we've read, so unread @mentions in this room stop
  // counting toward the app badge. Keyed on the newest message rather than
  // just mount: messages arriving while the room is open are read too, and
  // without this they'd start badging at the next refresh.
  //
  // In a thread, the same for the thread's read point (which doesn't touch the
  // room's: thread replies don't count toward room unread).
  const newestMessageAt = messages.length > 0 ? messages[messages.length - 1].timestamp : 0
  useEffect(() => {
    const upTo = newestMessageAt === 0 ? undefined : new Date(newestMessageAt).toISOString()
    if (threadRootID) void sendMarkChatThreadRead(threadRootID, upTo)
    else void sendMarkChatRoomRead(roomID, upTo)
  }, [roomID, threadRootID, newestMessageAt])

  // Open focused when asked (swipe-right / "Reply"), with the keyboard up: the
  // reply action primed it during the tap (keyboardPrimer), and focus moves
  // here. Tried now, next frame, and after the slide-in, since the screen is
  // still animating in; each try only takes focus nobody else has claimed, so
  // it never pulls focus away from something you clicked.
  useEffect(() => {
    if (!focusOnOpen) return
    const focusComposer = () => {
      const t = textareaRef.current
      const active = document.activeElement
      if (!t || active === t) return
      if (active && active !== document.body && !isKeyboardPrimer(active)) return
      t.focus({ preventScroll: true })
    }
    focusComposer()
    const frame = requestAnimationFrame(focusComposer)
    const afterSlide = setTimeout(focusComposer, 320) // NavStack's push is 0.28s
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(afterSlide)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Mark room as read on mount; clear badge. (The room's screen does this; a
  // thread over it has nothing to add.)
  useEffect(() => {
    if (inThread) return
    clearChatUnread(roomID)
    // Opening the room acknowledges its notifications — drop any delivered
    // OS banners for it (the tapped one plus siblings). Best-effort; no-op
    // on bridges without a native tray, and tolerant of older native shells
    // that don't implement the method yet.
    void bridge.call('notifications.clearForTarget', { roomID }).catch(() => {})
  }, [roomID]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load history on mount and whenever the WS reconnects. On a cold-launch
  // push tap the WS isn't open yet when the component mounts, so the initial
  // fetch fails silently — re-firing on 'connected' fills the gap.
  useEffect(() => {
    if (connectionStatus !== 'connected') return
    let cancelled = false
    void loadHistory(() => cancelled)
    return () => {
      cancelled = true
    }
  }, [connectionStatus, roomID, threadRootID]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load the latest page, tracking whether it worked. A response with no
  // message list is a FAILED load, not an empty room: saying "No messages yet"
  // about a room full of messages is how that failure hid (a reply mismatched
  // to the request came back without `messages`). One automatic retry, then a
  // visible "Try again". isCancelled stops a superseded load (room switched,
  // reconnected) from writing its outcome.
  async function loadHistory(isCancelled: () => boolean = () => false) {
    setLoadState('loading')
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const r = await fetchPage()
        if (isCancelled()) return
        if (r.error || !Array.isArray(r.messages)) {
          throw new Error(r.error || 'no messages in response')
        }
        ingest(r)
        setHasMore(!!r.has_more)
        setLoadState('loaded')
        return
      } catch {
        if (isCancelled()) return
        if (attempt === 0) await new Promise((res) => setTimeout(res, 1200))
      }
    }
    if (!isCancelled()) setLoadState('failed')
  }

  // One page of history: the channel, or this thread (its first message plus
  // replies). beforeID pages back.
  function fetchPage(beforeID?: string) {
    return threadRootID
      ? sendListChatThread(threadRootID, beforeID)
      : sendListChatMessages(roomID, beforeID)
  }

  // File a page into the room's one list (see threads.ts). A thread page also
  // carries its first message; refresh it too, since its summary may be newer
  // than the copy we hold.
  function ingest(r: { messages?: unknown[]; root?: unknown; following?: boolean }, prepend = false) {
    if (typeof r.following === 'boolean') setThreadFollowing(r.following)
    const toMsg = (m: WireChatMessage): ChatMessage => ({ ...m, timestamp: parseServerTime(m.created_at) })
    const page = ((r.messages ?? []) as WireChatMessage[]).map(toMsg)
    if (r.root) {
      upsertChatMessages(roomID, [toMsg(r.root as WireChatMessage)])
    }
    if (page.length === 0) return
    if (prepend) prependChatMessages(roomID, page)
    // Refresh rows already on screen too (a reload after being away brings new
    // thread summaries, edits, reactions); see upsertChatMessages.
    else upsertChatMessages(roomID, page)
  }

  // New-message auto-scroll is owned by useStickToBottom above (pins only when
  // the reader is already at the bottom; otherwise raises the "New messages"
  // pill). Scrolling the list container directly — rather than scrollIntoView on
  // a sentinel — matters on iOS, where scrollIntoView walks up to the visual
  // viewport and can shove the whole page (and the keyboard-lifted input)
  // off-screen; the hook does the same el.scrollTop write.

  // When hasMore becomes true the "Load earlier messages" button appears in a
  // separate render (Zustand + React state don't batch), adding ~40px at the
  // top after the scroll above already fired. Re-snap to bottom in that case.
  const prevHasMoreRef = useRef(false)
  useEffect(() => {
    if (hasMore === prevHasMoreRef.current) return
    prevHasMoreRef.current = hasMore
    if (!hasMore) return // going false = user exhausted history; don't jump
    requestAnimationFrame(() => {
      const el = scrollRef.current
      if (el) el.scrollTop = el.scrollHeight
    })
  }, [hasMore])

  // Auto-resize textarea.
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(144, Math.max(36, el.scrollHeight))}px`
  }, [text])

  async function loadMore() {
    if (loadingMore || messages.length === 0) return
    const firstID = messages[0].id
    setLoadingMore(true)
    // Capture scroll anchor: distance from the top of the current first
    // message to the top of the scroll container. After prepending older
    // messages we restore this so the view doesn't jump to the top.
    const anchor = scrollRef.current
      ? scrollRef.current.scrollTop
      : 0
    const prevScrollHeight = scrollRef.current?.scrollHeight ?? 0
    try {
      const r = await fetchPage(firstID)
      if (r.messages) {
        ingest(r, true)
        // Restore scroll position so prepended messages don't yank the view.
        requestAnimationFrame(() => {
          const el = scrollRef.current
          if (el) el.scrollTop = anchor + (el.scrollHeight - prevScrollHeight)
        })
      }
      setHasMore(!!r.has_more)
    } catch {
      // Ignore.
    } finally {
      setLoadingMore(false)
    }
  }

  async function ensureMembers(): Promise<MemberEntry[] | null> {
    if (members !== null) return members
    if (!organizationID || memberLoading) return null
    setMemberLoading(true)
    try {
      const r = await wsRequest<{ human_users?: { id: string; name: string; email?: string }[] }>(
        'list_human_users',
        { organization_id: organizationID },
        'human_users',
        { kind: 'organization', id: organizationID, action: 'list_human_users' },
      )
      // A live quick helper isn't someone you @: only @helper summons one.
      const agentEntries: MemberEntry[] = agentInstances
        .filter((a) => !a.retired_at && a.name && !a.chat_helper_room)
        .map((a) => ({ id: a.id, name: a.name, kind: 'agent' as const }))
      const self = (r.human_users ?? []).find((m) => m.id === humanUserID)
      if (self?.name || self?.email) setSelfName(self.name || self.email || null)
      const humanEntries: MemberEntry[] = (r.human_users ?? [])
        .filter((m) => m.id !== humanUserID && (m.name || m.email))
        .map((m) => ({ id: m.id, name: m.name || m.email || m.id, kind: 'human' as const }))
      const list = [...agentEntries, ...humanEntries]
      setMembers(list)
      return list
    } catch {
      setMembers([])
      return []
    } finally {
      setMemberLoading(false)
    }
  }

  function recomputeMentionState(value: string, caret: number) {
    let i = caret - 1
    while (i >= 0) {
      const ch = value[i]
      if (ch === '@') break
      if (/\s/.test(ch)) { setMentionState(null); return }
      i -= 1
    }
    if (i < 0) { setMentionState(null); return }
    if (i > 0 && !/\s/.test(value[i - 1])) { setMentionState(null); return }
    setMentionState({ anchor: i, query: value.slice(i + 1, caret) })
    setMentionHighlight(0)
    void ensureMembers()
  }

  const filteredMembers = useMemo(() => {
    if (!mentionState || !members) return []
    const q = mentionState.query.toLowerCase()
    // @helper is pinned first, so it's discoverable without knowing the word.
    const helper = canSummonHelper && HELPER_MEMBER.name.includes(q) ? [HELPER_MEMBER] : []
    if (!q) return [...helper, ...members].slice(0, 8)
    return [...helper, ...members.filter((m) => m.name.toLowerCase().includes(q))].slice(0, 8)
  }, [mentionState, members, canSummonHelper])

  // Resolve a mention ref (<@u:id>/<@a:id>) to its current display name + self
  // flag. Precise by (kind, id). The viewer is filtered out of the picker
  // roster, so their own mention resolves through a fallback label.
  const mentionResolver = useMemo<MentionResolver>(() => {
    const humans = new Map<string, string>()
    const agents = new Map<string, string>()
    for (const m of members ?? []) {
      ;(m.kind === 'agent' ? agents : humans).set(m.id, m.name)
    }
    return (kind, id) => {
      if (kind === 'h') return { name: HELPER_MEMBER.name, isSelf: false }
      if (kind === 'u') {
        const name = humans.get(id)
        if (name != null) return { name, isSelf: id === humanUserID }
        if (id === humanUserID) return { name: selfName ?? 'you', isSelf: true }
        return null
      }
      const name = agents.get(id)
      return name != null ? { name, isSelf: false } : null
    }
  }, [members, humanUserID, selfName])

  // Roster for the send-time @Name → id-ref transform. Humans first so a
  // human/agent name clash resolves to the human.
  // @helper resolves only when a helper could actually be spawned, otherwise it
  // stays plain text. It's listed first: the word is reserved for the summons.
  const sendRoster = useMemo<MentionMember[]>(() => {
    const list = members ?? []
    return [
      ...(canSummonHelper ? [HELPER_MEMBER] : []),
      ...list.filter((m) => m.kind === 'human'),
      ...list.filter((m) => m.kind === 'agent'),
    ]
  }, [members, canSummonHelper])

  // Load the roster when the room opens so mention chips resolve without the
  // user first opening the @-picker. ensureMembers early-returns once loaded.
  useEffect(() => {
    void ensureMembers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationID, roomID])

  function pickMention(member: MemberEntry) {
    if (!mentionState) return
    const el = textareaRef.current
    if (!el) return
    const token = '@' + member.name.replace(/\s+/g, '_')
    const before = text.slice(0, mentionState.anchor)
    const after = text.slice(mentionState.anchor + 1 + mentionState.query.length)
    const next = before + token + ' ' + after
    setText(next)
    setMentionState(null)
    const newCaret = before.length + token.length + 1
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(newCaret, newCaret)
    })
  }

  // Apply a text edit to the current textarea selection and restore the caret,
  // matching the pattern pickMention uses (mutate state, then re-set selection
  // on the next frame once React has re-rendered the controlled value).
  function editSelection(
    transform: (sel: { value: string; start: number; end: number }) => {
      value: string
      caretStart: number
      caretEnd: number
    },
  ) {
    const el = textareaRef.current
    const start = el?.selectionStart ?? text.length
    const end = el?.selectionEnd ?? text.length
    const { value, caretStart, caretEnd } = transform({ value: text, start, end })
    setText(value)
    requestAnimationFrame(() => {
      const t = textareaRef.current
      if (!t) return
      t.focus()
      t.setSelectionRange(caretStart, caretEnd)
    })
  }

  function applyFormat(kind: FormatKind) {
    editSelection(({ value, start, end }) => formatSelection(kind, value, start, end))
  }

  // Stable, so the picker's dismiss listeners aren't re-bound every render.
  const closeEmoji = useCallback(() => setShowEmoji(false), [])

  function insertEmoji(emoji: string) {
    editSelection(({ value, start, end }) => insertAtSelection(value, start, end, emoji))
  }

  // Upload one file to the server and stage it as a pending attachment. Bytes
  // persist server-side immediately; the message links them when the user hits
  // send. Used by the file picker, paste, and drag-drop.
  async function uploadFiles(files: File[]) {
    if (files.length === 0) return
    setAttachError(null)
    setUploading(true)
    try {
      for (const file of files) {
        // Transcode images to a downscaled JPEG so they render reliably (iOS
        // HEIC / oversized camera shots) and stay under the size cap.
        const prepared = await normalizeUpload(file)
        const dataURL = await new Promise<string>((res, rej) => {
          const reader = new FileReader()
          reader.onload = () => res(reader.result as string)
          reader.onerror = () => rej(reader.error)
          reader.readAsDataURL(prepared.blob)
        })
        const dataB64 = dataURL.slice(dataURL.indexOf(',') + 1)
        if (dataB64.length > MAX_ATTACHMENT_B64) {
          setAttachError(`"${prepared.filename}" is too large — max ~6 MB`)
          continue
        }
        const resp = await uploadChatAttachment(
          roomID,
          prepared.filename,
          prepared.mimeType,
          dataB64,
        )
        if (resp.error || !resp.attachment) {
          setAttachError(resp.error ?? 'Upload failed')
          continue
        }
        const att = resp.attachment
        setPendingAttachments((prev) => [...prev, att])
        bridge.call('haptic.light').catch(() => {})
      }
    } catch (err) {
      setAttachError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    ;(e.target as HTMLInputElement).value = ''
    void uploadFiles(files)
  }

  // Paste of an image (e.g. a screenshot) uploads it like a picked file.
  function handlePaste(e: React.ClipboardEvent) {
    const files = Array.from(e.clipboardData.files ?? [])
    if (files.length > 0) {
      e.preventDefault()
      void uploadFiles(files)
    }
  }

  function handleDrop(e: React.DragEvent) {
    const files = Array.from(e.dataTransfer.files ?? [])
    if (files.length > 0) {
      e.preventDefault()
      void uploadFiles(files)
    }
  }

  function removePendingAttachment(id: string) {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id))
  }

  // Load an existing message into the composer for editing. Only text is
  // editable; attachments/reply stay as they were.
  function startEdit(msg: ChatMessage) {
    setEditingMsg(msg)
    setPendingAttachments([])
    // Show @Name tokens in the editor, not the raw <@id> refs.
    const editable = refsToDisplay(msg.text, mentionResolver)
    setText(editable)
    requestAnimationFrame(() => {
      const el = textareaRef.current
      if (!el) return
      el.focus()
      const n = editable.length
      el.setSelectionRange(n, n)
    })
  }

  function cancelEdit() {
    setEditingMsg(null)
    // Put back the draft that was in the composer before the edit began.
    setText(getChatDraft(dKey))
  }

  // Keep this room's draft current. Edit mode is excluded: the composer then
  // holds an existing message's text, which is not a draft. If the screen is
  // reused for a different room, load that room's draft instead of saving this
  // text under it.
  const draftRoomRef = useRef(dKey)
  useEffect(() => {
    if (draftRoomRef.current !== dKey) {
      draftRoomRef.current = dKey
      setText(getChatDraft(dKey))
      return
    }
    if (!editingMsg) setChatDraft(dKey, text)
  }, [dKey, text, editingMsg])

  async function send() {
    const trimmed = text.trim()

    // Edit mode: apply the change to the existing message rather than posting.
    if (editingMsg) {
      if (!trimmed || sending) return
      const target = editingMsg
      const prevText = target.text
      // Convert @Name tokens back to id refs for storage/render.
      const refText = displayTokensToRefs(trimmed, sendRoster)
      setSending(true)
      setEditingMsg(null)
      // Back to the draft that was in progress before the edit.
      setText(getChatDraft(dKey))
      setMentionState(null)
      // Reflect the edit immediately; the server broadcast confirms it.
      updateChatMessage(roomID, target.id, {
        text: refText,
        edited_at: new Date().toISOString(),
      })
      try {
        await editChatMessage(target.id, refText)
      } catch {
        // Revert and re-open the editor with the attempted (display) text.
        updateChatMessage(roomID, target.id, { text: prevText })
        setEditingMsg(target)
        setText(trimmed)
      } finally {
        setSending(false)
      }
      return
    }

    const atts = pendingAttachments
    // A message needs text or at least one attachment; block while an upload is
    // still in flight so we don't post before its id exists.
    if ((!trimmed && atts.length === 0) || uploading) return

    const refText = displayTokensToRefs(trimmed, sendRoster)
    // Quote-reply is retired in favour of threads (docs/chat-threads-plan.md,
    // decision 1). Old quote-replies still render their quote.
    const thread = threadRootID ? { rootID: threadRootID, alsoInChannel } : null

    // Render the message immediately as an optimistic bubble (Slack-style) and
    // free the composer — the user shouldn't wait on a round trip to keep typing.
    // The bubble carries a local id + send_state; deliverMessage swaps it for the
    // authoritative server row on ack, or flips it to 'failed' (Retry / Delete) if
    // the send doesn't land. This is what makes a dropped send visible instead of
    // silently vanishing.
    const tempID = 'local-' + localID()
    const optimistic: ChatMessage = {
      id: tempID,
      room_id: roomID,
      organization_id: organizationID ?? '',
      sender_kind: 'human',
      sender_id: humanUserID ?? '',
      sender_name: 'You', // never shown for the `mine` variant
      text: refText,
      thread_root_id: thread?.rootID ?? null,
      also_in_channel: thread?.alsoInChannel || undefined,
      attachments: atts,
      timestamp: Date.now(),
      seq: Number.MAX_SAFE_INTEGER, // sort to the bottom until confirmed
      send_state: 'sending',
    }
    appendChatMessages(roomID, [optimistic])

    setText('')
    setMentionState(null)
    setShowPreview(false)
    setPendingAttachments([])
    setAlsoInChannel(false)

    await deliverMessage(tempID, refText, null, atts.map((a) => a.id), thread)
  }

  // Push an optimistic message to the relay and reconcile the bubble with the
  // outcome. Shared by first send and Retry. Never throws — failure is reflected
  // in the bubble's send_state, not surfaced as an unhandled rejection.
  async function deliverMessage(
    tempID: string,
    refText: string,
    replyID: string | null,
    attachmentIDs: string[],
    thread: { rootID: string; alsoInChannel?: boolean } | null,
  ) {
    updateChatMessage(roomID, tempID, { send_state: 'sending' })
    try {
      const res = await sendChatMessage(roomID, refText, replyID, attachmentIDs, thread)
      const real = res.message as WireChatMessage | undefined
      if (real) {
        confirmOptimisticMessage(roomID, tempID, {
          ...real,
          timestamp: parseServerTime(real.created_at),
        })
      } else {
        // Ack without a body (older server): drop the placeholder and let the
        // server's chat_message broadcast supply the authoritative row.
        removeChatMessage(roomID, tempID)
      }
    } catch {
      updateChatMessage(roomID, tempID, { send_state: 'failed' })
    }
  }

  // Retry a failed optimistic bubble in place — reuses the message's stored text,
  // reply target, and already-uploaded attachment ids (the bytes live server-side
  // from the first upload, so their ids are still valid).
  function retryMessage(m: ChatMessage) {
    void deliverMessage(
      m.id,
      m.text,
      m.reply_to_id ?? null,
      (m.attachments ?? []).map((a) => a.id),
      m.thread_root_id ? { rootID: m.thread_root_id, alsoInChannel: !!m.also_in_channel } : null,
    )
  }

  // Discard a failed optimistic bubble. It never reached the server, so there's
  // nothing to unsend — just drop the local row.
  function discardMessage(m: ChatMessage) {
    removeChatMessage(roomID, m.id)
  }

  // "Also send to #room" after the fact applies to your own live thread reply
  // that's still thread-only.
  function canSendToChannel(m: ChatMessage): boolean {
    return (
      inThread &&
      !!m.thread_root_id &&
      !m.also_in_channel &&
      !m.deleted_at &&
      !m.send_state &&
      m.sender_kind === 'human' &&
      m.sender_id === humanUserID
    )
  }

  // Optimistic: it shows as sent to the room at once; reverted with a toast if
  // the server refuses.
  async function replyToChannel(m: ChatMessage) {
    updateChatMessage(roomID, m.id, { also_in_channel: true })
    try {
      await sendChatReplyToChannel(m.id)
      pushToast(`Sent to #${roomName}`)
    } catch {
      updateChatMessage(roomID, m.id, { also_in_channel: false })
      pushToast('Couldn’t send it to the room. Try again.', 'warning')
    }
  }

  // Thread screen's bell: turn notifications for this thread's replies off or
  // on. Optimistic, reverted if the server refuses; a toast says what changed,
  // since the icon alone is easy to miss.
  async function toggleThreadFollow() {
    if (!threadRootID || threadFollowing === null) return
    const next = !threadFollowing
    setThreadFollowing(next)
    bridge.call('haptic.light').catch(() => {})
    try {
      setThreadFollowing(await sendSetChatThreadFollow(threadRootID, next))
      pushToast(
        next
          ? 'You’ll be notified about new replies'
          : 'Notifications off for this thread. Replying turns them back on.',
      )
    } catch {
      setThreadFollowing(!next)
      pushToast('Couldn’t change notifications. Try again.', 'warning')
    }
  }

  // Open a message's thread, full screen. Threads are one level deep, so a
  // reply (e.g. one also sent to the channel) opens the thread it belongs to.
  // focus=true opens with the composer focused: swipe-right and "Reply in
  // thread" mean "I want to reply".
  function openThread(msg: ChatMessage, focus: boolean) {
    const rootID = msg.thread_root_id ?? msg.id
    // On a wide window this room stays visible beside an open thread, so you
    // can open a DIFFERENT thread from it: swap the panel rather than stacking
    // a second thread (Back should return to the room, as in Slack). On a
    // phone the room is covered while a thread is open, so this never arises.
    const top = useAppStore.getState().navStack.at(-1)
    if (top?.kind === 'chat_thread' && top.roomID === roomID) {
      if (top.rootID === rootID) return // already showing it
      popNav()
    }
    // Replying: bring the keyboard up now, inside the tap, for the reply box
    // the thread is about to show (see keyboardPrimer).
    if (focus) primeKeyboard()
    pushNav({ kind: 'chat_thread', roomID, roomName, rootID, focus })
  }

  // React to a message, enforcing one emoji per person: picking a new emoji
  // replaces your previous one; picking the one you already have clears it. The
  // optimistic update swaps in the new reaction set immediately; the WS calls
  // mirror it (remove the old, add the new) so the server converges to the same
  // single-emoji state even though its model would allow more. Shared by the
  // reaction pills and the action menu.
  function reactToMessage(msgID: string, emoji: string) {
    // Search the whole room list: in a thread, the first message isn't among
    // the replies but can be reacted to.
    const msg = (allMessages ?? []).find((m) => m.id === msgID)
    if (!msg || !humanUserID) return
    const prev = myReactionEmoji(msg.reactions, humanUserID)
    const next = prev === emoji ? null : emoji
    bridge.call('haptic.light').catch(() => {})
    updateChatMessage(roomID, msgID, {
      reactions: setMyReaction(msg.reactions, humanUserID, next),
    })
    // Server reconciliation: drop the emoji I'm leaving, add the one I chose.
    if (prev && prev !== emoji) removeChatReaction(msgID, prev).catch(() => {})
    if (next) {
      addChatReaction(msgID, next).catch(() => {})
    } else {
      removeChatReaction(msgID, emoji).catch(() => {})
    }
  }

  const hasText = text.trim().length > 0
  const canSend = (hasText || pendingAttachments.length > 0) && !sending && !uploading

  return (
    <div className="chat-room">
      <NavBar
        title={inThread ? 'Thread' : roomName}
        subtitle={inThread ? roomName : undefined}
        leading={
          asPane ? (
            <NavBarButton icon="✕" label="Close thread" onClick={popNav} />
          ) : (
            <NavBarButton icon="‹" label="Back" onClick={popNav} />
          )
        }
        trailing={
          inThread ? (
            threadFollowing === null ? undefined : (
              <NavBarButton
                icon={<span style={{ fontSize: 20 }}>{threadFollowing ? '🔔' : '🔕'}</span>}
                label={
                  threadFollowing
                    ? 'Turn off notifications for this thread'
                    : 'Get notified about new replies'
                }
                onClick={() => void toggleThreadFollow()}
              />
            )
          ) : (
            <NavBarButton
              icon={<span style={{ fontSize: 22 }}>⚙︎</span>}
              label="Chat settings"
              onClick={() => pushSheet({ kind: 'chat_settings', roomID, roomName })}
            />
          )
        }
      />

      <div className="chat-room__body">
      <div className="chat-room__scroll" ref={scrollRef} onScroll={onScroll}>
        {hasMore && (
          <button
            className="chat-room__load-more"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load earlier messages'}
          </button>
        )}

        {inThread ? (
          // Thread: its first message, a "N replies" divider, then the replies.
          // One MessageList so date headers aren't repeated.
          threadRoot ? (
            <MessageList
              messages={[threadRoot, ...messages]}
              myUserID={humanUserID ?? ''}
              mentionResolver={mentionResolver}
              inThread
              dividerAfterID={messages.length > 0 ? threadRoot.id : undefined}
              dividerLabel={replyCountLabel(messages.length)}
              onRetry={retryMessage}
              onDiscard={discardMessage}
              onOpenActions={setActiveMsg}
              onReactionTap={(msg, emoji) => reactToMessage(msg.id, emoji)}
            />
          ) : loadState === 'failed' ? (
            <LoadFailed what="this thread" onRetry={() => void loadHistory()} />
          ) : (
            <div className="chat-room__empty">
              <div className="chat-room__empty-text">Loading thread…</div>
            </div>
          )
        ) : messages.length === 0 ? (
          loadState === 'loading' ? (
            <div className="chat-room__empty">
              <div className="chat-room__empty-text">Loading messages…</div>
            </div>
          ) : loadState === 'failed' ? (
            <LoadFailed what="messages" onRetry={() => void loadHistory()} />
          ) : (
            <div className="chat-room__empty">
              <div className="chat-room__empty-icon">💬</div>
              <div className="chat-room__empty-text">No messages yet. Say hello!</div>
            </div>
          )
        ) : (
          <MessageList
            messages={messages}
            myUserID={humanUserID ?? ''}
            mentionResolver={mentionResolver}
            onRetry={retryMessage}
            onDiscard={discardMessage}
            onOpenActions={setActiveMsg}
            onOpenThread={openThread}
            unreadThreadRoots={unreadThreadRoots}
            onReactionTap={(msg, emoji) => reactToMessage(msg.id, emoji)}
          />
        )}
      </div>
        {hasNew && (
          <button
            type="button"
            className="chat-room__jump"
            onClick={() => scrollToBottom()}
          >
            <span className="chat-room__jump-arrow" aria-hidden>↓</span>
            New messages
          </button>
        )}
      </div>

      {/* Visually hidden rather than display:none — iOS WKWebView can refuse to
          open the native picker for a programmatic .click() on a display:none
          file input, but honors it for one that's rendered but off-screen. */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', border: 0, padding: 0 }}
        onChange={handleFileSelected}
      />
      <div className="chat-room__input" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
        <div className="chat-room__input-divider" />
        {attachError && (
          <div style={{ fontSize: 12, color: 'var(--danger, #ff3b30)', padding: '4px 12px 0' }}>
            {attachError}
          </div>
        )}

        {pendingAttachments.length > 0 && (
          <div className="chat-room__pending">
            {pendingAttachments.map((att) => (
              <div key={att.id} className="chat-room__pending-item">
                {att.kind === 'image' ? (
                  <PendingImagePreview att={att} />
                ) : (
                  <span className="chat-room__pending-file">{att.filename}</span>
                )}
                <button
                  type="button"
                  className="chat-room__pending-remove"
                  onClick={() => removePendingAttachment(att.id)}
                  aria-label={`Remove ${att.filename}`}
                >
                  ✕
                </button>
              </div>
            ))}
            {uploading && <div className="chat-room__pending-item chat-room__pending-item--uploading" />}
          </div>
        )}

        {editingMsg && (
          <div className="msg-reply-strip">
            <div className="msg-reply-strip__content">
              <span className="msg-reply-strip__icon">✎</span>
              <div className="msg-reply-strip__text">
                <span className="msg-reply-strip__sender">Editing message</span>
                <span className="msg-reply-strip__preview">
                  {editingMsg.text.slice(0, 80)}{editingMsg.text.length > 80 ? '…' : ''}
                </span>
              </div>
            </div>
            <button
              className="msg-reply-strip__close"
              onClick={cancelEdit}
              aria-label="Cancel edit"
            >
              ✕
            </button>
          </div>
        )}

        {inThread && !editingMsg && (
          <label className="chat-room__also-send">
            <input
              type="checkbox"
              checked={alsoInChannel}
              onChange={(e) => setAlsoInChannel(e.target.checked)}
            />
            Also send to #{roomName}
          </label>
        )}

        {mentionState && filteredMembers.length > 0 && (
          <div className="inputbar__mention-pop" role="listbox">
            {filteredMembers.map((m, i) => (
              <button
                key={m.id}
                type="button"
                role="option"
                aria-selected={i === mentionHighlight}
                className={`inputbar__mention-item${i === mentionHighlight ? ' inputbar__mention-item--active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); pickMention(m) }}
              >
                <span className="inputbar__mention-name">
                  {m.kind !== 'human' && <StarIcon className="chat-bubble__agent-badge-icon" />}
                  {m.name}
                </span>
                {m.kind === 'helper' && (
                  <span className="inputbar__mention-hint">a fresh agent that answers, then leaves</span>
                )}
              </button>
            ))}
          </div>
        )}
        {showEmoji && (
          <EmojiPicker
            onPick={(emoji) => {
              // One pick, then out of the way — the caret is back in the
              // composer (editSelection refocuses it).
              insertEmoji(emoji)
              setShowEmoji(false)
            }}
            onClose={closeEmoji}
          />
        )}
        {showFormatBar && (
          <FormatBar
            onFormat={applyFormat}
            previewActive={showPreview}
            onTogglePreview={() => setShowPreview((v) => !v)}
          />
        )}

        <div className="chat-room__input-row">
          {!editingMsg && (
            <button
              className="chat-room__attach"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || sending}
              aria-label="Attach file"
            >
              {uploading ? '…' : '📎'}
            </button>
          )}
          <button
            className={`chat-room__tool${showEmoji ? ' chat-room__tool--active' : ''}`}
            onClick={() => { setShowEmoji((v) => !v); setShowFormatBar(false); setShowPreview(false) }}
            aria-label="Emoji"
            data-emoji-toggle
          >
            😀
          </button>
          <button
            className={`chat-room__tool${showFormatBar ? ' chat-room__tool--active' : ''}`}
            onClick={() => { setShowFormatBar((v) => { const nv = !v; if (!nv) setShowPreview(false); return nv }); setShowEmoji(false) }}
            aria-label="Formatting"
          >
            <span style={{ fontWeight: 700, fontSize: 15 }}>Aa</span>
          </button>
          {showPreview ? (
            <div className="chat-room__preview">
              {text.trim() ? (
                // Convert @Name tokens → id refs so mentions render as chips,
                // exactly as the sent message will.
                <Markdown
                  source={displayTokensToRefs(text, sendRoster)}
                  mentionResolver={mentionResolver}
                />
              ) : (
                <span className="chat-room__preview-empty">Nothing to preview</span>
              )}
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              className="chat-room__textarea"
              placeholder={inThread ? 'Reply…' : 'Message…'}
              value={text}
              rows={1}
              onPaste={handlePaste}
              onChange={(e) => {
                setText(e.target.value)
                recomputeMentionState(e.target.value, e.target.selectionStart ?? 0)
              }}
              onClick={(e) => {
                recomputeMentionState(e.currentTarget.value, e.currentTarget.selectionStart ?? 0)
              }}
              onBlur={() => setTimeout(() => setMentionState(null), 100)}
              onKeyDown={(e) => {
                if (mentionState && filteredMembers.length > 0) {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setMentionHighlight((i) => (i + 1) % filteredMembers.length); return }
                  if (e.key === 'ArrowUp') { e.preventDefault(); setMentionHighlight((i) => (i - 1 + filteredMembers.length) % filteredMembers.length); return }
                  if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(filteredMembers[mentionHighlight]); return }
                  if (e.key === 'Escape') { e.preventDefault(); setMentionState(null); return }
                }
                // Desktop markdown shortcuts (⌘/Ctrl+B / I / K). Enter-to-send
                // stays below since this only matches letter keys.
                if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey) {
                  const k = e.key.toLowerCase()
                  if (k === 'b') { e.preventDefault(); applyFormat('bold'); return }
                  if (k === 'i') { e.preventDefault(); applyFormat('italic'); return }
                  if (k === 'k') { e.preventDefault(); applyFormat('link'); return }
                }
                // Send on Enter. Browser (desktop): a bare Enter sends and
                // Shift+Enter inserts a newline. In the native app (isNative) the
                // on-screen-keyboard Enter stays a newline; ⌘/Ctrl+Enter sends
                // everywhere as a backstop. Never send mid-IME-composition —
                // Enter there commits the candidate. Mirrors InputBar.tsx.
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                  if (!bridge.isNative && !e.shiftKey) {
                    e.preventDefault()
                    void send()
                  } else if (e.metaKey || e.ctrlKey) {
                    e.preventDefault()
                    void send()
                  }
                }
              }}
            />
          )}
          <button
            className={`chat-room__send${canSend ? ' chat-room__send--active' : ''}`}
            onClick={() => void send()}
            disabled={!canSend}
            aria-label="Send"
          >
            ↑
          </button>
        </div>
      </div>

      {activeMsg && (
        <MessageActionMenu
          message={activeMsg}
          myUserID={humanUserID ?? ''}
          onClose={() => setActiveMsg(null)}
          // Threads are one level deep: no "Reply in thread" inside one.
          onReply={inThread ? undefined : () => openThread(activeMsg, true)}
          onEdit={() => startEdit(activeMsg)}
          onReact={(emoji) => reactToMessage(activeMsg.id, emoji)}
          sendToChannel={
            canSendToChannel(activeMsg)
              ? { label: `Send to #${roomName}`, onSelect: () => void replyToChannel(activeMsg) }
              : undefined
          }
        />
      )}
    </div>
  )
}

// Shown when a room's or thread's first load failed after its retry — instead
// of an empty screen that looks like there's nothing there.
function LoadFailed({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <div className="chat-room__empty">
      <div className="chat-room__empty-text">Couldn’t load {what}.</div>
      <button type="button" className="chat-room__load-more" onClick={onRetry}>
        Try again
      </button>
    </div>
  )
}

function MessageList({
  messages,
  myUserID,
  mentionResolver,
  inThread,
  dividerAfterID,
  dividerLabel,
  onRetry,
  onDiscard,
  onOpenActions,
  onOpenThread,
  unreadThreadRoots,
  onReactionTap,
}: {
  messages: ChatMessage[]
  myUserID: string
  mentionResolver: MentionResolver
  // Rendering inside a thread: no thread summaries or thread-opening gestures
  // (threads are one level deep).
  inThread?: boolean
  // In a thread: a "N replies" divider after its first message.
  dividerAfterID?: string
  dividerLabel?: string
  onRetry: (msg: ChatMessage) => void
  onDiscard: (msg: ChatMessage) => void
  onOpenActions: (msg: ChatMessage) => void
  // In the channel: open a message's thread (focus = start replying).
  onOpenThread?: (msg: ChatMessage, focus: boolean) => void
  // Roots of followed threads with unread replies (their summaries show so).
  unreadThreadRoots?: Set<string>
  onReactionTap: (msg: ChatMessage, emoji: string) => void
}) {
  const groups = groupByDate(messages)

  return (
    <div className="chat-room__messages">
      {groups.map((group) => (
        <div key={group.dateLabel}>
          <div className="chat-room__date-header">{group.dateLabel}</div>
          {group.messages.map((m) => {
            const isMe = m.sender_kind === 'human' && m.sender_id === myUserID
            const isAgent = m.sender_kind === 'agent'
            return (
              <Fragment key={m.id}>
                <ChatMessageRow
                  message={m}
                  isMe={isMe}
                  isAgent={isAgent}
                  myUserID={myUserID}
                  mentionResolver={mentionResolver}
                  inThread={inThread}
                  onRetry={() => onRetry(m)}
                  onDiscard={() => onDiscard(m)}
                  onOpenActions={() => onOpenActions(m)}
                  onOpenThread={onOpenThread ? (focus) => onOpenThread(m, focus) : undefined}
                  threadUnread={!!unreadThreadRoots?.has(m.id)}
                  onReactionTap={(emoji) => onReactionTap(m, emoji)}
                />
                {m.id === dividerAfterID && (
                  <div className="chat-thread__divider">
                    <span>{dividerLabel}</span>
                  </div>
                )}
              </Fragment>
            )
          })}
        </div>
      ))}
    </div>
  )
}

function ChatMessageRow({
  message,
  isMe,
  isAgent,
  myUserID,
  mentionResolver,
  inThread,
  onRetry,
  onDiscard,
  onOpenActions,
  onOpenThread,
  threadUnread,
  onReactionTap,
}: {
  message: ChatMessage
  isMe: boolean
  isAgent: boolean
  myUserID: string
  mentionResolver: MentionResolver
  inThread?: boolean
  onRetry: () => void
  onDiscard: () => void
  onOpenActions: () => void
  onOpenThread?: (focus: boolean) => void
  threadUnread?: boolean
  onReactionTap: (emoji: string) => void
}) {
  const rel = relTime(message.timestamp)
  // Append an "edited" marker to the timestamp for edited, non-deleted messages.
  const timeLabel = message.edited_at && !message.deleted_at ? `${rel} · edited` : rel
  const isDeleted = !!message.deleted_at
  const variant: BubbleVariant = isDeleted
    ? 'deleted'
    : isMe
      ? 'mine'
      : isAgent
        ? 'agent'
        : 'theirs'

  const reactions =
    message.reactions && message.reactions.length > 0 ? (
      <div className="msg-reaction-pills">
        {message.reactions.map((r) => {
          const byMe = r.reactor_ids.includes(myUserID)
          return (
            <button
              key={r.emoji}
              className={`msg-reaction-pill${byMe ? ' msg-reaction-pill--mine' : ''}`}
              onClick={() => onReactionTap(r.emoji)}
              aria-label={`${r.emoji} ${r.count}`}
            >
              {r.emoji} {r.count}
            </button>
          )
        })}
      </div>
    ) : null

  // Accent a bubble that @mentions me (but not my own messages).
  const highlight = !isMe && !isDeleted && mentionsSelf(message.text, mentionResolver)

  // Optimistic-send status (only ever on my own just-sent messages). 'sending'
  // shows a quiet label; 'failed' shows a red line with Retry / Delete so a
  // dropped send is impossible to miss and trivial to recover.
  const sendStatus =
    message.send_state === 'sending' ? (
      <div className="msg-send-status msg-send-status--sending">Sending…</div>
    ) : message.send_state === 'failed' ? (
      <div className="msg-send-status msg-send-status--failed">
        <span className="msg-send-status__label">Not delivered</span>
        <button type="button" className="msg-send-status__action" onClick={onRetry}>
          Retry
        </button>
        <button type="button" className="msg-send-status__action" onClick={onDiscard}>
          Delete
        </button>
      </div>
    ) : null

  // In the channel, a message with replies shows its thread summary. It stays
  // tappable even when the message itself was deleted: the thread survives
  // under the tombstone, as in Slack.
  const summary =
    !inThread && onOpenThread && message.thread && message.thread.reply_count > 0 ? (
      <ThreadSummaryRow
        summary={message.thread}
        lastReplyAgo={relTime(parseServerTime(message.thread.last_reply_at))}
        unread={threadUnread}
        onOpen={() => onOpenThread(false)}
      />
    ) : null

  // Inside a thread, a reply that was also sent to the channel says so.
  const alsoSentNote =
    inThread && message.also_in_channel ? (
      <div className="msg-thread-also-sent">Also sent to the channel</div>
    ) : null

  const below =
    reactions || summary || alsoSentNote || sendStatus ? (
      <>
        {reactions}
        {summary}
        {alsoSentNote}
        {sendStatus}
      </>
    ) : null

  // Gestures apply only to real, server-backed, non-deleted messages: an
  // in-flight/failed optimistic row has no server id yet (its Retry/Delete
  // affordance handles it instead), and a tombstone has nothing to act on. A
  // plain tap does nothing by design. In the channel, swipe-right opens the
  // message's thread to reply; inside a thread there's no swipe (one level).
  // Long-press opens the action menu everywhere.
  const gesturesEnabled = !message.send_state && !isDeleted

  const bubble = (
    <MessageBubble
      variant={variant}
      senderName={isMe ? undefined : message.sender_name}
      time={timeLabel}
      highlight={highlight}
      belowBubble={below}
    >
      {/* In the channel, a thread reply that was also sent here says where it
          came from; tapping that opens the thread. */}
      {!inThread && message.also_in_channel && message.thread_root_id && onOpenThread && (
        <button type="button" className="msg-thread-origin" onClick={() => onOpenThread(false)}>
          <span className="msg-thread-origin__label">replied to a thread:</span>{' '}
          <span className="msg-thread-origin__text">
            {message.thread_root_preview
              ? refsToDisplay(message.thread_root_preview.text, mentionResolver)
              : 'a message'}
          </span>
        </button>
      )}
      {message.reply_to_preview && (
        <div className="msg-reply-quote">
          <span className="msg-reply-quote__sender">{message.reply_to_preview.sender_name}</span>
          <span className="msg-reply-quote__text">
            {refsToDisplay(message.reply_to_preview.text, mentionResolver)}
          </span>
        </div>
      )}
      {isDeleted ? (
        <span className="chat-bubble__deleted">This message was deleted</span>
      ) : (
        <>
          {/* Every message — human or agent — renders as Markdown. Markdown is
              the declared lingua franca for chat now: a human typing **bold**, a
              list, a code block, or a link gets it rendered the same as an agent
              does. The renderer is XSS-safe (no raw HTML) and scheme-gates links.
              Mention refs (<@id>) resolve to @name chips via mentionResolver.
              Skip the block entirely for an attachments-only message (empty
              text) so an empty bubble doesn't render. */}
          {message.text.trim() && (
            <div className="chat-bubble__md">
              <Markdown source={message.text} mentionResolver={mentionResolver} />
            </div>
          )}
          {message.attachments && message.attachments.length > 0 && (
            <MessageAttachments attachments={message.attachments} />
          )}
        </>
      )}
    </MessageBubble>
  )

  if (!gesturesEnabled) return bubble
  return (
    <SwipeToReply
      onReply={!inThread && onOpenThread ? () => onOpenThread(true) : undefined}
      onLongPress={onOpenActions}
    >
      {bubble}
    </SwipeToReply>
  )
}

type DateGroup = { dateLabel: string; messages: ChatMessage[] }

function groupByDate(messages: ChatMessage[]): DateGroup[] {
  const groups: DateGroup[] = []
  let current: DateGroup | null = null
  for (const m of messages) {
    const label = dateLabel(m.timestamp)
    if (!current || current.dateLabel !== label) {
      current = { dateLabel: label, messages: [] }
      groups.push(current)
    }
    current.messages.push(m)
  }
  return groups
}

function dateLabel(ts: number): string {
  const d = new Date(ts)
  const now = new Date()
  if (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  ) {
    return 'Today'
  }
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate()
  ) {
    return 'Yesterday'
  }
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  })
}

function relTime(ts: number): string {
  const diff = Date.now() - ts
  const mins = Math.round(diff / 60_000)
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h`
  const days = Math.round(hrs / 24)
  return `${days}d`
}
